<template>
  <div class="view active">
    <div class="view-header">
      <h1>Copper <span class="ticker">HG/USD</span></h1>
      <p class="subtitle">Industrial Momentum Context</p>
    </div>

    <div v-if="copperPrice.price" class="price-display">
      <span class="big-price">${{ copperPrice.price?.toLocaleString(undefined, { maximumFractionDigits: 2 }) }}</span>
      <span class="big-change" :class="copperPrice.change >= 0 ? 'up' : 'down'">
        {{ copperPrice.change >= 0 ? '+' : '' }}{{ copperPrice.change?.toFixed(2) }} ({{ copperPrice.changePercent?.toFixed(2) }}%)
      </span>
    </div>

    <div class="chart-section">
      <div class="chart-header">
        <div class="chart-title-stack">
          <h2>Price Chart</h2>
          <p>Copper &bull; {{ rangeLabel }} Performance</p>
        </div>
        <div class="chart-range-switcher">
          <button :class="{ active: chartRange === '7d' }" @click="setRange('7d')">7D</button>
          <button :class="{ active: chartRange === '30d' }" @click="setRange('30d')">30D</button>
          <button :class="{ active: chartRange === '90d' }" @click="setRange('90d')">90D</button>
          <button :class="{ active: chartRange === '1y' }" @click="setRange('1y')">1Y</button>
          <button :class="{ active: chartRange === '5y' }" @click="setRange('5y')">5Y</button>
          <button :class="{ active: chartRange === '10y' }" @click="setRange('10y')">10Y</button>
        </div>
      </div>
      <div class="chart-wrapper">
        <FinanceChart :data="chartPoints" color="#f97316" :price-precision="2" label="Copper" />
      </div>
    </div>

    <div class="overview-research">
      <div class="recommendation-panel">
        <div class="section-header">
          <h2>Timing Windows</h2>
        </div>
        <TimingWindowTable :windows="timingWindows" />
      </div>
    </div>

    <div class="recommendation-panel" style="margin-top: 1rem;">
      <div class="section-header">
        <h2>Validated Copper Strategies</h2>
      </div>
      <p class="section-intro">
        Mars-Venus conjunctions with 7-day MFE-optimized holds. Vedic Taamra Murthi framework.
        April-May 2026 backtest: 4/4 wins (100% win rate, ₹2,43,731 profit).
      </p>
      <div class="strategies-grid">
        <div class="strategy-card highlight">
          <h3 class="strategy-name">Mars-Venus Conjunction</h3>
          <div class="strategy-stats">
            <span class="stat">Win Rate: <strong class="text-success">79.4%</strong></span>
            <span class="stat">Baseline: <strong class="text-success">5% in 21d</strong></span>
            <span class="stat">Hold: <strong>7 days (MFE-optimized)</strong></span>
          </div>
          <p class="strategy-desc">
            Primary copper signal. Mars-Venus conjunctions create favorable conditions for copper rallies.
            7-day hold optimized for maximum favorable excursion (+2.73% avg vs +0.38% for 14-day).
          </p>
          <div class="next-signal">
            <strong>April-May 2026 Validation:</strong> 4/4 wins, 100% win rate
          </div>
        </div>

        <div class="strategy-card">
          <h3 class="strategy-name">Mars in Aries/Scorpio</h3>
          <div class="strategy-stats">
            <span class="stat">Win Rate: <strong class="text-success">High</strong></span>
            <span class="stat">Hold: <strong>5-7 days</strong></span>
          </div>
          <p class="strategy-desc">
            Mars in own signs (Aries, Scorpio) provides strong directional energy for copper.
            Industrial metal responds to Mars aggression/energy themes.
          </p>
        </div>

        <div class="strategy-card">
          <h3 class="strategy-name">Mercury-Venus Conjunction</h3>
          <div class="strategy-stats">
            <span class="stat">Win Rate: <strong class="text-success">85.7%</strong></span>
            <span class="stat">Hold: <strong>3-5 days</strong></span>
          </div>
          <p class="strategy-desc">
            Secondary signal for quick trades. Mercury-Venus conjunctions in earth signs
            favor industrial metal demand cycles.
          </p>
        </div>
      </div>
    </div>

    <div class="intelligence-briefing">
      <span class="briefing-kicker">Subjective Intelligence Report</span>
      <div class="briefing-title">Industrial Momentum Context</div>
      <div class="briefing-content">
        Copper is evaluated as an industrial cyclical metal. Premium timing windows align with scheduled manufacturing demand shifts. Require price confirmation and strict 7-day exits.
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useMarketStore } from '../stores/market';
import TimingWindowTable from '../components/TimingWindowTable.vue';
import FinanceChart from '../components/FinanceChart.vue';

const marketStore = useMarketStore();
const chartRange = ref('30d');

const copperPrice = computed(() => marketStore.prices.copper || { price: 0, change: 0, changePercent: 0 });

const timingWindows = ref([
  // === APRIL-MAY 2026 ===
  { type: 'BUY', startDate: '2026-04-17', endDate: '2026-04-23', signal: 'Mars Aries + Venus Taurus + 4 Conjunctions', score: '63', expected: '+2.8%', winRate: '79.4%' },
  { type: 'BUY', startDate: '2026-05-08', endDate: '2026-05-16', signal: 'Venus-Mars sextile + Moon Aries/Taurus', score: '40', expected: '+2.3%', winRate: '65%' },
  { type: 'WATCH', startDate: '2026-04-24', endDate: '2026-05-07', signal: 'Venus leaves Taurus Apr 24 — copper signal weakens', score: '25', expected: '-' },
  // === JUNE 2026 ===
  { type: 'AVOID', startDate: '2026-06-01', endDate: '2026-06-12', signal: 'Mars in Taurus + Venus in Cancer · ALL LOW', score: '5-15', expected: '-' },
  { type: 'WATCH', startDate: '2026-06-13', endDate: '2026-06-28', signal: 'Venus in Leo (dignified +10) but Mars still Taurus · Still LOW', score: '15-18', expected: '-' },
  { type: 'AVOID', startDate: '2026-06-29', endDate: '2026-06-30', signal: 'Mars enters Gemini — no rulership · No copper trade', score: '-', expected: '-' },
]);

const rangeLabel = computed(() => {
  const map = { '7d': '7 Day', '30d': '30 Day', '90d': '90 Day', '1y': '1 Year', '5y': '5 Year', '10y': '10 Year' };
  return map[chartRange.value] || '30 Day';
});

const getDaysForRange = () => {
  switch (chartRange.value) {
    case '7d': return 7;
    case '30d': return 30;
    case '90d': return 90;
    case '1y': return 365;
    case '5y': return 1825;
    case '10y': return 3650;
    default: return 30;
  }
};

const getFetchDaysForRange = () => {
  switch (chartRange.value) {
    case '7d': return 30;
    case '30d': return 90;
    case '90d': return 120;
    case '1y': return 400;
    case '5y': return 1900;
    case '10y': return 3700;
    default: return 90;
  }
};

const setRange = async (range) => {
  chartRange.value = range;
  const fetchDays = getFetchDaysForRange();
  const currentDataLen = marketStore.commodityData.copper?.chartData?.length || 0;
  if (currentDataLen < fetchDays * 0.8) {
    await marketStore.fetchCommodityData('copper', fetchDays);
  }
};

const chartPoints = computed(() => {
  const raw = marketStore.commodityData.copper?.chartData || [];
  if (!raw.length) return [];
  const days = getDaysForRange();
  const sliced = raw.slice(-days);
  return sliced.map(p => ({
    time: p.date,
    value: Number(p.close),
  }));
});

onMounted(async () => {
  await marketStore.fetchCommodityData('copper', 400);
});
</script>

<style scoped>
.intelligence-briefing {
  background: linear-gradient(135deg, rgba(248, 244, 235, 0.98), rgba(255, 255, 255, 0.98));
  border: 1px solid rgba(249, 115, 22, 0.22);
  border-radius: 16px;
  padding: 1.25rem 1.35rem;
  margin-top: 1.5rem;
  box-shadow: 0 4px 12px rgba(249, 115, 22, 0.08);
}

.briefing-kicker {
  display: block;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-dim);
  margin-bottom: 0.5rem;
}

.briefing-title {
  font-size: 1.08rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.65rem;
  letter-spacing: -0.01em;
}

.briefing-content {
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--text-secondary);
}

.briefing-content strong {
  color: #f97316;
  font-weight: 600;
}

.chart-title-stack p {
  margin-top: 0.3rem;
  color: var(--text-secondary);
  font-size: 0.82rem;
}

.chart-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 320px;
  color: var(--text-muted);
  font-size: 0.9rem;
}

.section-intro {
  color: var(--text-secondary);
  font-size: 0.85rem;
  line-height: 1.6;
  margin-bottom: 1rem;
}

.strategies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.strategy-card {
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 4px 12px rgba(29, 36, 48, 0.06);
  transition: all 0.2s ease;
}

.strategy-card:hover {
  border-color: rgba(249, 115, 22, 0.32);
  box-shadow: 0 6px 18px rgba(249, 115, 22, 0.12);
}

.strategy-card.highlight {
  border-left: 3px solid #f97316;
  background: rgba(249, 115, 22, 0.04);
}

.strategy-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.75rem;
  letter-spacing: -0.01em;
}

.strategy-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.8rem;
}

.stat {
  color: var(--text-secondary);
}

.stat strong {
  color: var(--text-primary);
}

.strategy-desc {
  color: var(--text-secondary);
  font-size: 0.85rem;
  line-height: 1.6;
  margin-bottom: 0.75rem;
}

.next-signal {
  font-size: 0.8rem;
  color: #f97316;
  font-family: var(--font-mono);
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--border-color);
}

.text-success { color: var(--bullish); }
.text-warning { color: var(--warning); }
.text-danger { color: var(--danger); }
</style>
